import { motion } from "framer-motion";
import { MessageCircle, Send, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import * as AuthAction from "../../store/actions/auth/AuthAction";
import * as DataAction from "../../store/actions/data/DataAction";
import { GetCurrentActiveChatSession } from "../../store/constants/DataActionConst";
import RatingPopup from "../common/RatingsPopup";

import socket from "../../config/socketIoConfig";
import BaseUrl from "../../constants/BaseUrl";
import { ShowError, ShowSuccess } from "../../utils/ToasFunction";

function Card({ children }) {
  return (
    <div className="bg-white shadow-lg rounded-xl overflow-hidden">
      {children}
    </div>
  );
}

function Button({ children, onClick }) {
  return (
    <motion.button
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      onClick={onClick}
      className="bg-[#0381CA] text-white p-3 rounded-full shadow-xl hover:shadow-2xl focus:outline-none"
    >
      {children}
    </motion.button>
  );
}

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [rating, setRating] = useState({
    number: 0,
    show: false,
    sessionRated: false, // Track if current session has been rated
  });
  const [suggestMsg] = useState([
    {
      msg: "📄 How can I track lease expiration dates and renewals?",
    },
    {
      msg: "💰 How do I set up automated rent collection and invoicing?",
    },
    {
      msg: "📝 Can I store and retrieve lease agreements digitally?",
    },
    {
      msg: "📊 How do I generate rent roll and financial reports?",
    },
    {
      msg: "💳 Does the system support online rent payments and late fee automation?",
    },
    {
      msg: "💸 How can I track security deposits and refunds?",
    },
    {
      msg: "🔧 How do I create and track maintenance requests?",
    },
    {
      msg: "📅 Can tenants submit service requests online?",
    },
    {
      msg: "📈 Can I generate occupancy and vacancy reports?",
    },
    {
      msg: "💼 How do I track property performance metrics?",
    },
  ]);
  const messagesEndRef = useRef(null); // Reference for scrolling to the bottom
  const { loginSuccess, userCompleteProfile } = useSelector(
    (state) => state.auth
  );
  const { currentActiveSession, currentActiveRatingSession } = useSelector(
    (state) => state.data
  );
  const dispatch = useDispatch();

  // Scroll to the bottom whenever messages are updated or currentActiveSession changes
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentActiveSession?.conversation]);

  // Scroll to the bottom when the chat is opened
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [isOpen]);

  const GetUpdateChat = useCallback(() => {
    const currentData = new Date();
    const formattedDate = currentData?.toISOString()?.split("T")[0];
    dispatch(
      DataAction.CurrentActiveChatSession(
        userCompleteProfile?.personalInformation?._id,
        formattedDate,
        loginSuccess
      )
    );
  }, [dispatch, userCompleteProfile?.personalInformation?._id, loginSuccess]);

  // Reset rating state when starting a new chat session
  useEffect(() => {
    if (currentActiveSession?._id) {
      setRating(prev => ({ ...prev, sessionRated: false }));
    }
  }, [currentActiveSession?._id]);

  useEffect(() => {
    GetUpdateChat();
  }, [GetUpdateChat]);

  useEffect(() => {
    if (!userCompleteProfile?.landloardId) return;
    const userId = userCompleteProfile.landloardId;

    if (!socket.connected) {
      console.log("Socket not connected, connecting...");
      socket.connect();
    } else {
      console.log("Socket already connected");
    }

    console.log("Socket connection status:", socket.connected);
    console.log(socket, "Socket ID");
    console.log("Setting up socket listeners for userId:", userId);

    // Join the customer's room
    socket.emit("joinRoom", userId);
    console.log("Emitted joinRoom for:", userId);

    // Add connection event listeners for debugging
    socket.on("connect", () => {
      console.log("Socket connected with ID:", socket.id);
    });

    socket.on("disconnect", () => {
      console.log("Socket disconnected");
    });

    socket.on("error", (error) => {
      console.error("Socket error:", error);
    });

    // Listen for incoming messages
    const handleReceiveMessage = (messageObj) => {
      console.log("Message received:", messageObj);
      GetUpdateChat(); // Refresh the chat
    };

    const handleCompletedMessage = (messageObj) => {
      console.log("Rating triggered", messageObj);
      console.log("Current messageObj customerfeedback:", messageObj?.customerfeedback);
      console.log("Should show rating popup:", !messageObj?.customerfeedback || messageObj.customerfeedback === 0);
      
      // Always show rating popup if no rating has been given yet in the message object
      if (!messageObj?.customerfeedback || messageObj.customerfeedback === 0) {
        console.log("Setting rating popup to show");
        setRating(prev => ({ ...prev, number: 0, show: true, sessionRated: false }));
      } else {
        console.log("Rating already exists, not showing popup");
      }
      
      dispatch({
        type: GetCurrentActiveChatSession.GET_ACTIVE_CHAT_SESSION_RAT,
        data: messageObj,
      });
    };

    socket.on("chat:receive", handleReceiveMessage);
    socket.on("chat:completed", handleCompletedMessage);
    
    console.log("Socket listeners attached for chat:receive and chat:completed");

    return () => {
      console.log("Cleaning up socket listeners");
      socket.off("chat:receive", handleReceiveMessage);
      socket.off("chat:completed", handleCompletedMessage);
    };
  }, [userCompleteProfile, GetUpdateChat, dispatch]);

  const UpdateChatRatingOnly = async (rating) => {
    if (isSending) return; // Prevent multiple submissions

    if (!userCompleteProfile) {
      console.log("here");
      await dispatch(
        AuthAction.GetLandlordProfile(
          loginSuccess?.user?._id,
          loginSuccess.token
        )
      );
    }

    setIsSending(true); // Set loading state

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

    const raw = JSON.stringify({
      customerfeedback: rating,
    });

    const requestOptions = {
      method: "PUT",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    fetch(
      `${BaseUrl}api/support/updateChatSession/${
        currentActiveSession?._id || currentActiveRatingSession?._id
      }`,
      requestOptions
    )
      .then((response) => response.json())
      .then((result) => {
        if (result.status === "Success") {
          GetUpdateChat();
          ShowSuccess("Rating submitted successfully");
        } else {
          ShowError("Oops! something went wrong.");
        }
      })
      .catch((error) => console.error(error))
      .finally(() => {
        setIsSending(false); // Reset loading state
      });
  };

  const UpdateChatMessage = async (rating) => {
    if (isSending) return; // Prevent multiple submissions

    if (!userCompleteProfile) {
      console.log("here");
      await dispatch(
        AuthAction.GetLandlordProfile(
          loginSuccess?.user?._id,
          loginSuccess.token
        )
      );
    }

    setIsSending(true); // Set loading state

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

    const messageObj = {
      id: userCompleteProfile?.personalInformation?._id,
      message: input,
      fullName: `${userCompleteProfile?.personalInformation?.firstName} ${userCompleteProfile?.personalInformation?.lastName}`,
      dateTime: new Date(),
      support: false,
    };

    const raw = JSON.stringify({
      chatObje: messageObj,
      ...(rating > 0 && { customerfeedback: rating }),
    });

    const requestOptions = {
      method: "PUT",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    fetch(
      `${BaseUrl}api/support/updateChatSession/${
        currentActiveSession?._id || currentActiveRatingSession?._id
      }`,
      requestOptions
    )
      .then((response) => response.json())
      .then((result) => {
        if (result.status === "Success") {
          // Emit socket event after successful update
          const receiverId = result.data?.supportStaffId || null;
          if (receiverId) {
            socket.emit("chat:new", {
              to: receiverId,
              messageObj,
            });
          }
          GetUpdateChat();
          setInput("");
        } else {
          ShowError("Oops! something went wrong.");
        }
      })
      .catch((error) => console.error(error))
      .finally(() => {
        setIsSending(false); // Reset loading state
      });
  };

  const CreateSupportSession = () => {
    if (isSending) return; // Prevent multiple submissions

    const currentData = new Date();
    const formattedDate = currentData?.toISOString()?.split("T")[0];

    setIsSending(true); // Set loading state

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

    const messageObj = {
      id: userCompleteProfile?.personalInformation?._id,
      message: input,
      fullName: `${userCompleteProfile?.personalInformation?.firstName} ${userCompleteProfile?.personalInformation?.lastName}`,
      dateTime: new Date(),
      support: false,
    };

    const raw = JSON.stringify({
      customerData: userCompleteProfile.personalInformation,
      conversation: [messageObj],
      createDate: String(formattedDate),
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    fetch(`${BaseUrl}api/support/createChatSession`, requestOptions)
      .then((response) => response.json())
      .then((result) => {
        if (result.status === "Success") {
          // Emit socket event after creation
          const receiverId = result.data?.supportStaffId || null;
          if (receiverId) {
            socket.emit("chat:new", {
              to: receiverId,
              messageObj: result.data, // Send the whole chat session
            });
          }
          ShowSuccess("Chat Session created successfully");
          GetUpdateChat();
          setInput("");
        } else {
          ShowError(result?.message);
        }
      })
      .catch((error) => console.error(error))
      .finally(() => {
        setIsSending(false); // Reset loading state
      });
  };
  const handleSendMessage = () => {
    if (input.trim() === "" || isSending) {
      if (input.trim() === "") {
        ShowError("Please enter any message first");
      }
      return;
    }

    if (currentActiveSession) {
      UpdateChatMessage();
    } else {
      CreateSupportSession();
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      GetUpdateChat();
    }, 3000);

    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, [GetUpdateChat]);

  const onSubmitRating = (ratingValue) => {
    if (userCompleteProfile?.personalInformation?._id) {
      setRating({ number: ratingValue, show: false, sessionRated: true });
      
      // Only update chat with rating if there's no active input message
      // This prevents sending empty messages after rating
      if (input.trim() !== "") {
        UpdateChatMessage(ratingValue);
      } else {
        // Just submit the rating without a message
        UpdateChatRatingOnly(ratingValue);
      }
      
      setIsOpen(false);
    } else {
      ShowError("Chat session not ready. Please try again.");
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end">
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="mb-4 w-[400px] shadow-2xl"
        >
          {/* <button onClick={GetUpdateChat}>Update</button> */}
          <Card>
            <div className="flex items-center justify-between bg-[#0381CA] p-3 text-white">
              <span className="font-popinsBold">Chatbot</span>
              <motion.div whileHover={{ scale: 1.2 }}>
                <X
                  className="cursor-pointer"
                  onClick={() => setIsOpen(false)}
                />
              </motion.div>
            </div>
            <div className="flex flex-wrap h-40 overflow-y-auto panelScroll pr-4 border-b-2 border-black/10">
              {suggestMsg?.map((item, index) => (
                <div
                  onClick={() => {
                    setInput(item.msg);
                  }}
                  className="bg-[#F4F4F4]  my-2 p-3 ml-4 text-black text-xs cursor-pointer font-popinsMedium self-end rounded-l-lg rounded-tl-lg rounded-br-lg"
                >
                  <p>{item.msg}</p>
                </div>
              ))}
            </div>

            <div className="h-80 overflow-y-auto panelScroll p-4 bg-white flex flex-col space-y-3">
              {currentActiveSession?.conversation?.map((msg, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }} // Slight delay for smoother display
                  className={`p-3 max-w-[75%] shadow-md text-white font-popinsRegular ${
                    !msg.support
                      ? "bg-[#0381CA] self-end rounded-l-lg rounded-tl-lg rounded-br-lg"
                      : "bg-gray-400 self-start rounded-r-lg rounded-tr-lg rounded-bl-lg"
                  }`}
                >
                  {msg?.message}
                </motion.div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            {rating.show && (
              <>
                <div className="flex justify-center w-full">
                  <RatingPopup
                    show={rating.show}
                    onClose={() => setRating({ number: 0, show: false, sessionRated: rating.sessionRated })}
                    ratingNumber={rating.number}
                    onSubmit={(rating) => {
                      onSubmitRating(rating);
                    }}
                  />
                </div>
              </>
            )}
            <div className="flex items-center border-t p-3 bg-gray-100">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message..."
                disabled={isSending}
                className="flex-1 p-2 font-popinsRegular border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0381CA] disabled:opacity-50 disabled:cursor-not-allowed"
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault(); // Prevent default behavior
                    handleSendMessage();
                  }
                }}
              />

              <motion.button
                whileHover={{ scale: isSending ? 1 : 1.2 }}
                whileTap={{ scale: isSending ? 1 : 0.9 }}
                onClick={handleSendMessage}
                disabled={isSending}
                className={`ml-3 ${
                  isSending
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-[#0381CA] hover:text-[#0381CA]/90"
                }`}
              >
                <Send size={28} />
              </motion.button>
            </div>
          </Card>
        </motion.div>
      )}
      <motion.div
        whileHover={{ scale: 1.2 }}
        whileTap={{ scale: 0.9 }}
        animate={{
          scale: isOpen ? 1.1 : 1,
          rotate: isOpen ? 45 : 0,
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 20,
        }}
      >
        <Button onClick={() => setIsOpen(!isOpen)}>
          <MessageCircle size={26} />
        </Button>
      </motion.div>
    </div>
  );
}
